import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import classNames from 'classnames';

import { Box } from 'ui/box';

import { useFeatureFlags } from 'components/shared/feature-flags';

import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';
import { Typography } from 'ui/typography';
import Tabs from 'components/partials/tabs/tabs';

import { useCurrentUser } from 'hooks/api/use-current-user';

import GoalsPanel from 'containers/user/critical-elements-monitoring/components/goals-panel';
import MonitoringDashboard from 'containers/user/critical-elements-monitoring/monitoring-dashboard';
import {
  MONITORING_GOALS_TAB_VALUE,
  MONITORING_TAB_QUERY_KEY,
} from 'containers/user/critical-elements-monitoring/monitoring-constants';

const CRITICAL_ELEMENTS_TAB_VALUE = 'critical-elements';
const CRITICAL_ELEMENTS_PANEL_ID = 'monitoring-critical-elements-panel';
const GOALS_PANEL_ID = 'monitoring-goals-panel';
const MONITORING_TABS = [
  {
    value: CRITICAL_ELEMENTS_TAB_VALUE,
    label: 'Critical Elements',
    panelId: CRITICAL_ELEMENTS_PANEL_ID,
  },
  {
    value: MONITORING_GOALS_TAB_VALUE,
    label: 'Goals',
    panelId: GOALS_PANEL_ID,
  },
];

const CriticalElementsMonitoring = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { featureFlags } = useFeatureFlags();
  const hasGoalsTab = featureFlags['tab-goals'];
  const isGoalsTabActive =
    hasGoalsTab &&
    searchParams.get(MONITORING_TAB_QUERY_KEY) === MONITORING_GOALS_TAB_VALUE;
  const activeTab = isGoalsTabActive
    ? MONITORING_GOALS_TAB_VALUE
    : CRITICAL_ELEMENTS_TAB_VALUE;

  const [areTabsVisible, setAreTabsVisible] = useState(isGoalsTabActive);
  const currentUser = useCurrentUser();
  const clientId = currentUser.data?.client?.id
    ? String(currentUser.data.client.id)
    : '';

  const handleInitialLoadSettled = useCallback(
    () => setAreTabsVisible(true),
    []
  );

  const handleTabSelect = (tab: string): void => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);

        if (tab === MONITORING_GOALS_TAB_VALUE) {
          next.set(MONITORING_TAB_QUERY_KEY, tab);
        } else {
          next.delete(MONITORING_TAB_QUERY_KEY);
        }

        return next;
      },
      { replace: true }
    );
  };

  return (
    <ResponsiveWrapper customClass="h-full px-6">
      <Typography
        data-testid="monitoring-heading"
        variant="h2"
        component="h1"
        sx={{ mb: 1 }}
      >
        Monitoring
      </Typography>

      {hasGoalsTab && (
        <>
          <div className={classNames({ invisible: !areTabsVisible })}>
            <Tabs
              items={MONITORING_TABS}
              activeTab={activeTab}
              onSelect={handleTabSelect}
              customClass="mb-4 border-navy-100"
            />
          </div>

          <Box id={GOALS_PANEL_ID} role="tabpanel" hidden={!isGoalsTabActive}>
            {isGoalsTabActive && <GoalsPanel />}
          </Box>
        </>
      )}

      <Box
        id={CRITICAL_ELEMENTS_PANEL_ID}
        role="tabpanel"
        hidden={isGoalsTabActive}
      >
        <MonitoringDashboard
          clientId={clientId}
          isActive={!isGoalsTabActive}
          onInitialLoadSettled={handleInitialLoadSettled}
        />
      </Box>
    </ResponsiveWrapper>
  );
};

export default CriticalElementsMonitoring;
